import React, { useContext, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Socio, SocioServiceFirestore } from '../../services/SocioServiceFirestore';
import { FaSave, FaArrowLeft, FaGavel, FaUser, FaUsers } from '../../components/Icons';
import ImageUploader from '../../components/ImageUploader';
import { theme } from '../../theme';

const Container = styled.div`
  padding: 24px;
  max-width: 1280px;
  margin: 0 auto;
  background-color: ${theme.colors.background.default};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
`;

const Title = styled.h1`
  color: ${theme.colors.primary.main};
  font-weight: ${theme.typography.fontWeights.bold};
  font-size: 2.2rem;
  position: relative;
  padding-bottom: 12px;
  display: flex;
  align-items: center;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 60px;
    height: 4px;
    background-color: ${theme.colors.secondary.main};
    border-radius: 2px;
  }
  
  svg {
    margin-right: 16px;
    color: ${theme.colors.secondary.main};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
`;

const Button = styled.button`
  background-color: ${theme.colors.secondary.main};
  color: ${theme.colors.secondary.contrastText};
  border: none;
  border-radius: ${theme.borderRadius.small};
  padding: 12px 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  font-weight: ${theme.typography.fontWeights.medium};
  transition: all 0.3s ease;
  box-shadow: ${theme.shadows.small};
  
  &:hover {
    background-color: ${theme.colors.secondary.dark};
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.medium};
  }
  
  svg {
    margin-right: 10px;
    font-size: 1.1rem;
  }
`;

const BackButton = styled(Button)`
  background-color: ${theme.colors.primary.main};
  
  &:hover {
    background-color: ${theme.colors.primary.dark};
  }
`;

const Form = styled.form`
  background-color: ${theme.colors.background.paper};
  border-radius: ${theme.borderRadius.medium};
  padding: 28px;
  box-shadow: ${theme.shadows.medium};
  margin-bottom: 32px;
  border: 1px solid rgba(0, 0, 0, 0.05);
`;

const FormGroup = styled.div`
  margin-bottom: 24px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 10px;
  font-weight: ${theme.typography.fontWeights.semibold};
  color: ${theme.colors.text.primary};
  font-size: 1rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: ${theme.borderRadius.small};
  font-size: 16px;
  transition: all 0.3s ease;
  background-color: ${theme.colors.background.default};
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.secondary.main};
    box-shadow: 0 0 0 3px rgba(228, 49, 46, 0.15);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: ${theme.borderRadius.small};
  font-size: 16px;
  transition: all 0.3s ease;
  background-color: ${theme.colors.background.default};
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.secondary.main};
    box-shadow: 0 0 0 3px rgba(228, 49, 46, 0.15);
  }
`;

const Card = styled.div`
  background-color: ${theme.colors.background.paper};
  border-radius: ${theme.borderRadius.medium};
  padding: 28px;
  box-shadow: ${theme.shadows.medium};
  margin-bottom: 28px;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  border: 1px solid rgba(0, 0, 0, 0.05);
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: ${theme.shadows.large};
  }
`;

const CardTitle = styled.h2`
  color: ${theme.colors.primary.main};
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  font-size: 1.4rem;
  font-weight: ${theme.typography.fontWeights.semibold};
  
  svg {
    margin-right: 12px;
    color: ${theme.colors.secondary.main};
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  background-color: #0f3460;
  color: white;
  text-align: left;
  padding: 12px 15px;
`;

const Td = styled.td`
  padding: 12px 15px;
  border-bottom: 1px solid #ddd;
`;



const SocioDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [socio, setSocio] = useState<Socio | null>(null);
  const [formData, setFormData] = useState<any>({});

  const [equipos, setEquipos] = useState<any[]>([]);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  useEffect(() => {
    const fetchSocio = async () => {
      if (id === 'new') {
        setSocio(null);
        setFormData({
          Categoria: '',
          EQUIPO: '',
          GUID: '',
          H: '',
          NOMBRE_CORTO: '',
          NOMBRE_SERVERS_VRS: '',
          NOTAS: '',
          PAIS: '',
          SOCIO: '',
          display_name: '',
          ligaAsignada: '',
          user_activation_key: '',
          user_email: '',
          user_login: '',
          user_nicename: '',
          user_pass: '',
          user_registered: '',
          user_status: '',
          user_url: ''
        });
      } else {
        const socioFS = await SocioServiceFirestore.getById(id!);
        setSocio(socioFS);
        setFormData(socioFS || {});
      }
    };
    fetchSocio();
    // Cargar equipos
    import('../../services/EquipoServiceFirestore').then(({ EquipoService }) => {
      EquipoService.getAll().then(setEquipos);
    });
  }, [id]);

  // Manejar cambios en el formulario
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev: any) => ({
      ...prev,
      [name]: value
    }));
  };

  // Guardar socio
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    const requiredFields = ['Categoria', 'display_name', 'user_login', 'user_email'];
    const newErrors: { [key: string]: string } = {};
    requiredFields.forEach(field => {
      if (!formData[field] || formData[field].trim() === '') {
        newErrors[field] = 'Este campo es obligatorio';
      }
    });
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      return;
    }
    try {
      // Comprobar si ha cambiado el equipo
      const equipoAnterior = socio?.EQUIPO || '';
      const equipoNuevo = formData.EQUIPO || '';
      const hayCambioEquipo = equipoAnterior !== equipoNuevo;
      
      console.log(`DEBUG - SocioDetail - Guardando socio. Equipo anterior: "${equipoAnterior}", Equipo nuevo: "${equipoNuevo}"`);
      
      if (hayCambioEquipo) {
        // Importar EquipoService para actualizar los equipos
        const { EquipoService } = await import('../../services/EquipoServiceFirestore');
        
        // 1. Si el socio pertenecía a un equipo, eliminarlo de ese equipo
        if (equipoAnterior) {
          // Buscar el equipo anterior por nombre
          const equiposAnteriores = equipos.filter(eq => eq.nombre === equipoAnterior);
          for (const eqAnterior of equiposAnteriores) {
            console.log(`DEBUG - SocioDetail - Eliminando socio ${socio?.id} del equipo ${eqAnterior.nombre} (${eqAnterior.id})`);
            // Eliminar el socio del equipo
            const sociosActualizados = eqAnterior.socios.filter((socioId: string) => socioId !== socio?.id);
            await EquipoService.update(eqAnterior.id, { socios: sociosActualizados });
          }
        }
        
        // 2. Si el socio se asigna a un nuevo equipo, añadirlo a ese equipo
        if (equipoNuevo) {
          // Buscar el equipo nuevo por nombre
          const equiposNuevos = equipos.filter(eq => eq.nombre === equipoNuevo);
          for (const eqNuevo of equiposNuevos) {
            console.log(`DEBUG - SocioDetail - Añadiendo socio ${socio?.id} al equipo ${eqNuevo.nombre} (${eqNuevo.id})`);
            // Añadir el socio al equipo si no está ya
            if (!eqNuevo.socios.includes(socio?.id)) {
              const sociosActualizados = [...eqNuevo.socios, socio?.id];
              await EquipoService.update(eqNuevo.id, { socios: sociosActualizados });
            }
          }
        }
      }
      
      // Guardar los cambios del socio
      // Sincronizar fotoPerfil y fotoUrl para compatibilidad
      if ((formData.fotoUrl || formData.fotoPerfil) && formData.fotoUrl !== formData.fotoPerfil) {
        // Si se cambió uno, iguala ambos
        formData.fotoPerfil = formData.fotoUrl;
        formData.fotoUrl = formData.fotoPerfil;
      }
      if (!formData.fotoUrl) {
        formData.fotoUrl = '';
        formData.fotoPerfil = '';
      }
      if (id === 'new') {
        const nuevoSocio = await SocioServiceFirestore.create(formData);
        console.log(`DEBUG - SocioDetail - Socio creado con ID: ${nuevoSocio.id}`);
        
        // Si se asignó un equipo al nuevo socio, añadirlo al equipo
        if (equipoNuevo && nuevoSocio.id) {
          const { EquipoService } = await import('../../services/EquipoServiceFirestore');
          const equiposNuevos = equipos.filter(eq => eq.nombre === equipoNuevo);
          for (const eqNuevo of equiposNuevos) {
            console.log(`DEBUG - SocioDetail - Añadiendo nuevo socio ${nuevoSocio.id} al equipo ${eqNuevo.nombre} (${eqNuevo.id})`);
            const sociosActualizados = [...eqNuevo.socios, nuevoSocio.id];
            await EquipoService.update(eqNuevo.id, { socios: sociosActualizados });
          }
        }
      } else if (socio) {
        // Sincronizar fotoPerfil y fotoUrl para compatibilidad
        if (formData.fotoUrl && !formData.fotoPerfil) {
          formData.fotoPerfil = formData.fotoUrl;
        }
        if (formData.fotoPerfil && !formData.fotoUrl) {
          formData.fotoUrl = formData.fotoPerfil;
        }
        await SocioServiceFirestore.update(socio.id!, formData);
        console.log(`DEBUG - SocioDetail - Socio actualizado con ID: ${socio.id}`);
      }
      
      alert('Socio guardado correctamente');
      navigate('/socios');
    } catch (error) {
      console.error('ERROR - SocioDetail - Error al guardar socio:', error);
      alert('Error al guardar el socio. Por favor, inténtalo de nuevo.');
    }
  };

  // Obtener sanciones del socio
  const getSanciones = () => {
    if (!socio) return [];

    // TO DO: implementar lógica para obtener sanciones del socio
    return [];
  };

  // Obtener resultados del socio
  const getResultados = () => {
    if (!socio) return [];

    // TO DO: implementar lógica para obtener resultados del socio
    return [];
  };

  return (
    <Container>
      <Header>
        <Title>
          <FaUser /> {id === 'new' ? 'Nuevo Socio' : `Editar Socio: ${formData.display_name || 'Sin nombre'}`}
        </Title>
        <ButtonGroup>
          <BackButton onClick={() => navigate('/socios')}>
            <FaArrowLeft /> Volver
          </BackButton>
        </ButtonGroup>
      </Header>

      {/* Diseño tipo carnet moderno con foto, nombre y equipo */}
      <Card>
        <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>
          {/* Foto de perfil */}
          <div style={{ width: '180px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{
              width: '150px',
              height: '150px',
              borderRadius: '50%',
              overflow: 'hidden',
              border: '3px solid #fff',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              backgroundColor: '#f5f6fa',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '12px'
            }}>
              {formData.fotoUrl ? (
                <img 
                  src={formData.fotoUrl} 
                  alt="Foto de perfil" 
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              ) : (
                <FaUser style={{ fontSize: '60px', color: '#ccc' }} />
              )}
            </div>
            <div style={{ width: '100%' }}>
              <ImageUploader
                currentImageUrl={formData.fotoUrl}
                onImageChange={(imageUrl) => {
                  setFormData((prev: any) => ({
                    ...prev,
                    fotoUrl: imageUrl,
                    fotoPerfil: imageUrl
                  }));
                }}
                folder="pilotos"
                placeholder="URL de la foto..."
              />
              <div style={{ fontSize: '0.8rem', color: '#666', textAlign: 'center', marginTop: '4px' }}>
                Sube una foto o ingresa una URL
              </div>
            </div>
          </div>
          
          {/* Información del piloto */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <div style={{ marginBottom: '24px' }}>
              <CardTitle><FaUser /> Nombre de piloto</CardTitle>
              <div style={{
                fontSize: '2em', 
                fontWeight: 'bold', 
                color: theme.colors.primary.main, 
                marginBottom: '8px',
                borderBottom: `2px solid ${theme.colors.secondary.main}`,
                paddingBottom: '8px'
              }}>
                {formData.display_name || 'Sin nombre'}
              </div>
            </div>
            
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
              {/* Categoría */}
              <div style={{ minWidth: '150px' }}>
                <div style={{ fontWeight: 'bold', color: '#666', fontSize: '0.9rem', marginBottom: '4px' }}>
                  CATEGORÍA
                </div>
                <div style={{ 
                  padding: '6px 12px', 
                  backgroundColor: theme.colors.primary.main, 
                  color: '#fff', 
                  borderRadius: '4px',
                  display: 'inline-block'
                }}>
                  {formData.Categoria || 'Sin categoría'}
                </div>
              </div>
              
              {/* Equipo */}
              <div style={{ minWidth: '150px' }}>
                <div style={{ fontWeight: 'bold', color: '#666', fontSize: '0.9rem', marginBottom: '4px' }}>
                  EQUIPO
                </div>
                <div style={{ 
                  padding: '6px 12px', 
                  backgroundColor: theme.colors.secondary.main, 
                  color: '#fff', 
                  borderRadius: '4px',
                  display: 'inline-block'
                }}>
                  {formData.EQUIPO || 'Sin equipo'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card>
      <Form onSubmit={handleSubmit}>
        <CardTitle><FaUser /> Información del Socio</CardTitle>
        {/* Mover display_name al principio para darle prioridad y eliminar EQUIPO duplicado */}
        {['display_name', 'Categoria', 'GUID', 'H', 'NOMBRE_CORTO', 'NOMBRE_SERVERS_VRS', 'NOTAS', 'PAIS', 'SOCIO', 'ligaAsignada']
  .map((key) => (
            <FormGroup key={key}>
              <Label>{key.replace(/_/g, ' ')}{['Categoria','display_name','user_login','user_email'].includes(key) && <span style={{color: 'red'}}> *</span>}</Label>
              <Input
                type="text"
                name={key}
                value={formData[key] ?? ''}
                onChange={handleChange}
                required={['nombre','apellido','Categoria'].includes(key)}
                style={errors[key] ? {borderColor: 'red'} : {}}
              />
              {errors[key] && <div style={{color:'red', fontSize:12}}>{errors[key]}</div>}
            </FormGroup>
        ))}
        {/* El campo de foto de perfil se ha movido a la sección superior */}
        {/* Selector de equipo */}
        <FormGroup>
          <Label>Equipo</Label>
          <Select
            name="EQUIPO"
            value={formData.EQUIPO || ''}
            onChange={handleChange}
          >
            <option value="">Sin equipo</option>
            {equipos.map(eq => (
              <option key={eq.id} value={eq.nombre}>{eq.nombre}</option>
            ))}
          </Select>
        </FormGroup>
        
        <Button type="submit" style={{ marginTop: '20px' }}>
          <FaSave /> Guardar
        </Button>
      </Form>
      {/* La sección de Resultados y Sanciones ha sido eliminada ya que esta información se muestra en el perfil del piloto */}
    </Container>
  );
};

export default SocioDetail;
